import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { logError } from "@/lib/logger";
import { API } from "@/App";
import { ExternalLink, Globe, Loader2 } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";
import { LinkEditorDialog } from "@/components/live/LinkEditorDialog";
import { LiveItemControls, LiveAddButton, useLiveDelete } from "@/components/live/LiveItemControls";
import { Section } from "@/components/live/Section";

const Links = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const liveDelete = useLiveDelete();
  const [dialog, setDialog] = useState({ open: false, link: null });

  // Fallback data if API returns empty
  const fallbackLinks = [
    { name: "OnlyFans", url: "https://onlyfans.com", icon: "🔥", description: "Exclusive content and behind-the-scenes", category: "content" },
    { name: "Fansly", url: "https://fansly.com", icon: "💫", description: "Premium subscription content", category: "content" },
    { name: "Twitter / X", url: "https://twitter.com", icon: "𝕏", description: "Daily thoughts and updates", category: "social" },
    { name: "Instagram", url: "https://instagram.com", icon: "📸", description: "Curated photos and stories", category: "social" },
    { name: "Preferred411", url: "https://preferred411.com", icon: "✓", description: "Verified provider profile", category: "professional" },
    { name: "PrivateDelights", url: "https://privatedelights.ch", icon: "💎", description: "Reviews and references", category: "professional" },
  ];

  const categoryLabels = {
    content: { title: "Official Content", description: "My verified platforms and profiles" },
    social: { title: "Social Media", description: "Follow me across platforms" },
    professional: { title: "Professional Profiles", description: "Industry databases and directories" },
    wishlist: { title: "Full Wishlists", description: "Spoil me with gifts from my wishlists" },
    other: { title: "Other Links", description: "Additional resources" }
  };

  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/links`);
      if (response.data && response.data.length > 0) {
        setLinks(response.data);
      } else {
        setLinks(fallbackLinks);
      }
    } catch (error) {
      logError("Failed to fetch links, using fallback:", error);
      setLinks(fallbackLinks);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  // Group links by category
  const groupedLinks = links.reduce((acc, link) => {
    const category = link.category || "other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(link);
    return acc;
  }, {});

  return (
    <div data-testid="links-page">
      {/* Header */}
      <section className="page-header" data-testid="links-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="links.find-me-elsewhere" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Find Me Elsewhere</T>
          <T k="links.links-platforms" as="h1" className="page-title animate-fade-in stagger-1">Links & Platforms</T>
          <T k="links.connect-with-me" as="p" className="page-subtitle animate-fade-in stagger-2">Connect with me across the web — official profiles, content platforms, and social media.</T>
        </div>
      </section>

      {/* Links Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24" data-testid="links-grid">
        <div className="max-w-5xl mx-auto space-y-16">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-jade-text animate-spin" />
            </div>
          ) : (
            Object.entries(groupedLinks).map(([category, categoryLinks], catIndex) => {
              const categoryInfo = categoryLabels[category] || categoryLabels.other;
              return (
                <div key={category} data-testid={`link-category-${catIndex}`}>
                  <div className="text-center mb-10">
                    <T k={`links.cat.${category}.title`} as="h2" className="font-heading text-3xl text-charcoal mb-2">{categoryInfo.title}</T>
                    <T k={`links.cat.${category}.text`} as="p" className="font-body text-stone-grey">{categoryInfo.description}</T>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {categoryLinks.map((link, linkIndex) => (
                      <a
                        key={link.id || linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="glass rounded-xl p-6 card-hover group text-center relative"
                        data-testid={`link-${catIndex}-${linkIndex}`}
                      >
                        {link.id && <LiveItemControls id={link.id} onEdit={() => setDialog({ open: true, link })} onDelete={() => liveDelete(`links/${link.id}`, link.name, fetchLinks)} />}
                        <span className="text-4xl mb-4 block">{link.icon || "🔗"}</span>
                        <h3 className="font-heading text-lg text-charcoal mb-2 group-hover:text-jade-text transition-colors flex items-center justify-center gap-2">
                          {link.name}
                          <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </h3>
                        {link.description && (
                          <p className="font-body text-sm text-stone-grey">{link.description}</p>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })
          )}
          <div className="text-center">
            <LiveAddButton label="Add a link" testId="live-add-link" onClick={() => setDialog({ open: true, link: null })} />
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <Section k="links.section.links-disclaimer" label="links disclaimer" className="py-16 px-6 md:px-12 lg:px-24 bg-sand/30" data-testid="links-disclaimer">
        <div className="max-w-3xl mx-auto">
          <div className="glass rounded-xl p-8 text-center">
            <T k="links.verification-notice" as="h3" className="font-heading text-xl text-charcoal mb-4">Verification Notice</T>
            <T k="links.verification-text" as="p" multiline className="font-body text-stone-grey leading-relaxed">These are my only official profiles and platforms. If you encounter an account claiming to be me elsewhere, it may be impersonation. When in doubt, please reach out through this website to verify.</T>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <Globe className="w-12 h-12 text-champagne-gold mx-auto mb-6" />
          <T k="links.prefer-a-personal" as="h2" className="font-heading text-3xl text-charcoal mb-6">Prefer a Personal Connection?</T>
          <T k="links.while-i-love" as="p" multiline className="font-body text-stone-grey mb-8 max-w-2xl mx-auto">While I love connecting with fans online, nothing compares to real-world encounters. If you're interested in meeting me personally, start with a booking inquiry.</T>
          <T k="links.submit-inquiry" as="a" href="/booking" className="btn-primary inline-block">Submit Inquiry</T>
        </div>
      </section>
      <LinkEditorDialog open={dialog.open} link={dialog.link} onClose={() => setDialog({ open: false, link: null })} onSaved={fetchLinks} />
    </div>
  );
};

export default Links;
